import { useEffect, useRef, useState } from 'react'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Avatar from '@mui/material/Avatar'
import Chip from '@mui/material/Chip'
import Skeleton from '@mui/material/Skeleton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined'
import HourglassBottomOutlinedIcon from '@mui/icons-material/HourglassBottomOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import HelmetMeta from '../../layouts/HelmetMeta'
import { useEvent } from '../../context/DataContext'
import EventHeader from './EventHeader'
import EventDetails from './EventDetails'
import {
  CDN,
  EVENT_TZ,
  bannerPath,
  formatDateRange,
  formatDuration,
  formatPrice
} from './eventUtils'

dayjs.extend(utc)
dayjs.extend(timezone)

// Combine an event date with its "05:30 AM" style start time in the event timezone.
function parseEventStart (dateValue, startTime) {
  const base = dayjs(dateValue).tz(EVENT_TZ)
  const match = /(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(startTime || '')
  let hour = 0
  let minute = 0
  if (match) {
    hour = parseInt(match[1], 10)
    minute = parseInt(match[2], 10)
    if (match[3]) {
      hour = hour % 12
      if (/PM/i.test(match[3])) hour += 12
    }
  }
  return base.hour(hour).minute(minute).second(0).millisecond(0)
}

// Build schema.org Event structured data for the page.
function buildEventJsonLd (eventData, { imageUrl, paymentLink, pageUrl, price } = {}) {
  if (!eventData) return null

  const dates = Array.isArray(eventData.date) ? eventData.date.filter(Boolean) : (eventData.date ? [eventData.date] : [])
  const start = dates[0] ? parseEventStart(dates[0], eventData.startTime) : null
  const durationMinutes = parseInt(eventData.duration, 10)
  let end = null
  if (start) {
    if (Number.isFinite(durationMinutes) && durationMinutes > 0) {
      end = start.add(durationMinutes, 'minute')
    } else if (dates[1]) {
      end = dayjs(dates[1]).tz(EVENT_TZ).endOf('day')
    }
  }

  const organizers = (eventData.organizedBy || []).map(o => ({
    '@type': 'Organization',
    name: o.name,
    ...(o.instaSlug ? { sameAs: `https://instagram.com/${o.instaSlug}` } : {})
  }))

  const performers = (eventData.hostedBy || []).map(h => ({
    '@type': 'Person',
    name: h.name,
    ...(h.instaSlug ? { sameAs: `https://instagram.com/${h.instaSlug}` } : {})
  }))

  let geo = null
  const gps = eventData.location?.gpsLL
  if (typeof gps === 'string' && gps.includes(',')) {
    const [lat, lng] = gps.split(',').map(s => parseFloat(s.trim()))
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      geo = { '@type': 'GeoCoordinates', latitude: lat, longitude: lng }
    }
  }

  const location = eventData.location?.name
    ? { '@type': 'Place', name: eventData.location.name, ...(geo ? { geo } : {}) }
    : null

  const offer = Number.isFinite(price)
    ? {
        '@type': 'Offer',
        price: String(price),
        priceCurrency: 'AED',
        availability: eventData.status === 'open' ? 'https://schema.org/InStock' : 'https://schema.org/SoldOut',
        ...(paymentLink || pageUrl ? { url: paymentLink || pageUrl } : {})
      }
    : null

  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: eventData.title,
    ...(eventData.description || eventData.subtitle ? { description: eventData.description || eventData.subtitle } : {}),
    ...(start ? { startDate: start.format('YYYY-MM-DDTHH:mm:ssZ') } : {}),
    ...(end ? { endDate: end.format('YYYY-MM-DDTHH:mm:ssZ') } : {}),
    eventStatus: eventData.status === 'cancelled' ? 'https://schema.org/EventCancelled' : 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    ...(imageUrl ? { image: [imageUrl] } : {}),
    ...(location ? { location } : {}),
    ...(organizers.length ? { organizer: organizers.length === 1 ? organizers[0] : organizers } : {}),
    ...(performers.length ? { performer: performers.length === 1 ? performers[0] : performers } : {}),
    ...(offer ? { offers: offer } : {})
  }
}

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

function SectionCard ({ icon, title, children }) {
  return (
    <Paper
      component='section'
      sx={{
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'rgba(60,196,128,0.14)',
            color: 'primary.main',
            flexShrink: 0
          }}
        >
          {icon}
        </Box>
        <Typography variant='h6' component='h2' sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  )
}

function FactRow ({ icon, label, value }) {
  if (!value) return null
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ color: 'primary.main', mt: '1px', flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', letterSpacing: 0.4, textTransform: 'uppercase', fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant='body2' sx={{ color: 'text.primary', fontWeight: 500 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  )
}

function PersonRow ({ person }) {
  if (!person?.name) return null
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'rgba(255,255,255,0.02)',
        minWidth: 0
      }}
    >
      <Avatar src={person.avatar ? `${CDN}/img/events/${person.avatar}` : undefined} sx={{ width: 48, height: 48, flexShrink: 0 }}>
        {person.name.charAt(0)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant='body2' sx={{ fontWeight: 700 }} noWrap>
          {person.name}
        </Typography>
        {person.instaSlug && (
          <Typography variant='caption' sx={{ display: 'block' }}>
            <Box
              component='a'
              href={`https://instagram.com/${person.instaSlug}`}
              target='_blank'
              rel='noopener noreferrer'
              sx={{ color: 'secondary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              @{person.instaSlug}
            </Box>
          </Typography>
        )}
        {person.phone && (
          <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary' }}>
            <Box component='a' href={`tel:${person.phone.replace(/\s+/g, '')}`} sx={{ color: 'inherit', textDecoration: 'none' }}>
              {person.phone}
            </Box>
          </Typography>
        )}
        {person.email && (
          <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Box component='a' href={`mailto:${person.email}`} sx={{ color: 'inherit', textDecoration: 'none' }}>
              {person.email}
            </Box>
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default function EventPage () {
  const { t } = useTranslation('events')
  const navigate = useNavigate()
  const { name } = useParams()
  const [eventData, setEventData] = useState(null)
  const { event, loading } = useEvent(name)
  const [openConfirm, setOpenConfirm] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const actionBarRef = useRef(null)

  // Helper function to format early bird end date/time
  const formatEarlyBirdEnd = (endDateTime) => {
    if (!endDateTime) return ''
    const hasTime = endDateTime.includes('T') || endDateTime.length > 10
    if (hasTime) {
      return dayjs(endDateTime).tz(EVENT_TZ).format('h:mm A, D MMMM YYYY')
    }
    return t('earlyBirdEndOfDay', { date: dayjs(endDateTime).tz(EVENT_TZ).format('D MMMM YYYY') })
  }

  // Helper function to check if early bird is active
  const isEarlyBirdActive = (endDateTime) => {
    if (!endDateTime) return false
    const hasTime = endDateTime.includes('T') || endDateTime.length > 10
    const endDate = hasTime
      ? dayjs(endDateTime).tz(EVENT_TZ)
      : dayjs(endDateTime).tz(EVENT_TZ).endOf('day')
    return dayjs().tz(EVENT_TZ).isBefore(endDate)
  }

  // Helper function to get time remaining for early bird
  const getEarlyBirdTimeRemaining = (endDateTime) => {
    if (!endDateTime || !isEarlyBirdActive(endDateTime)) return null
    const hasTime = endDateTime.includes('T') || endDateTime.length > 10
    const endDate = hasTime
      ? dayjs(endDateTime).tz(EVENT_TZ)
      : dayjs(endDateTime).tz(EVENT_TZ).endOf('day')
    const diffMs = endDate.diff(dayjs().tz(EVENT_TZ))
    if (diffMs <= 0) return null
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }

  // Helper function to check if registrations are closed
  const areRegistrationsClosed = (e) => {
    if (!e) return true
    if (e.status === 'closed' || e.status === 'ended') return true
    if (e.registrationsClosed) return true
    if (e.regCloseHoursBeforeStart && e.date?.[0] && e.startTime) {
      const eventDateTime = dayjs(e.date[0])
        .hour(parseInt(e.startTime.split(':')[0]))
        .minute(e.startTime.includes('AM') || e.startTime.includes('PM') ? 0 : parseInt(e.startTime.split(':')[1]))
        .tz(EVENT_TZ)
      const registrationCloseTime = eventDateTime.subtract(e.regCloseHoursBeforeStart, 'hour')
      return dayjs().tz(EVENT_TZ).isAfter(registrationCloseTime)
    }
    return false
  }

  // Helper function to determine pricing status
  const getPricingStatus = (e) => {
    const earlyBirdActive = e?.earlyBirdPrice && isEarlyBirdActive(e.earlyBirdEndDateTime)
    const closed = areRegistrationsClosed(e)
    if (closed) return 'closed'
    if (earlyBirdActive) return 'earlyBird'
    if (e?.regularPrice) return 'regular'
    return 'none'
  }

  useEffect(() => {
    if (loading) return
    if (name && event) {
      setEventData(event)
    } else {
      navigate('/not-found')
    }
  }, [name, event, loading, navigate])

  // Expose the mobile action bar height so fixed elements (e.g. the language
  // switcher) can sit above it instead of overlapping the Reserve button.
  useEffect(() => {
    const height = actionBarRef.current ? actionBarRef.current.offsetHeight : 0
    document.documentElement.style.setProperty('--bottom-action-bar', `${height}px`)
    return () => document.documentElement.style.removeProperty('--bottom-action-bar')
  }, [eventData, loading])

  const handleShare = async () => {
    const url = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({ title: eventData?.title || t('shareTitle'), url })
      } catch (err) {
        console.log('Share failed:', err)
      }
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(
        () => alert(t('linkCopied')),
        (err) => console.error('Failed to copy: ', err)
      )
    } else {
      const textArea = document.createElement('textarea')
      textArea.value = url
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      alert(t('linkCopied'))
    }
  }

  if (loading || !eventData) {
    return (
      <Container maxWidth='lg' sx={{ py: { xs: 7, md: 12 } }}>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Skeleton variant='rectangular' height={320} />
          <Skeleton variant='rectangular' height={200} />
          <Skeleton variant='rectangular' height={200} />
        </Box>
      </Container>
    )
  }

  const pricingStatus = getPricingStatus(eventData)
  const earlyBirdTimeRemaining = eventData?.earlyBirdPrice ? getEarlyBirdTimeRemaining(eventData.earlyBirdEndDateTime) : null
  const earlyBirdEndFormatted = eventData?.earlyBirdPrice ? formatEarlyBirdEnd(eventData.earlyBirdEndDateTime) : null
  const paymentLink = pricingStatus === 'earlyBird'
    ? eventData?.earlyBirdPaymentLink
    : eventData?.regularPaymentLink

  const reserveNotice = eventData?.reserveNotice
  const bringItems = Array.isArray(reserveNotice?.bring) ? reserveNotice.bring.filter(Boolean) : []
  const hasReserveNotice = bringItems.length > 0 || !!reserveNotice?.important

  const activePrice = pricingStatus === 'earlyBird' ? eventData.earlyBirdPrice : eventData.regularPrice
  const strikePrice = pricingStatus === 'earlyBird' && eventData.regularPrice ? eventData.regularPrice : null

  const dateText = formatDateRange(eventData.date)
  const durationText = formatDuration(eventData.duration)

  const sharePath = bannerPath(eventData.socialShareBanner) || '/img/peaks-splash.jpg'

  const jsonLd = buildEventJsonLd(eventData, {
    imageUrl: `${CDN}${sharePath}`,
    paymentLink,
    pageUrl: window.location.href,
    price: pricingStatus === 'earlyBird' && eventData.earlyBirdPrice
      ? Number(eventData.earlyBirdPrice) / 100
      : (eventData.regularPrice ? Number(eventData.regularPrice) / 100 : null)
  })

  const reserveButtonProps = hasReserveNotice
    ? { onClick: () => { setAcknowledged(false); setOpenConfirm(true) } }
    : { href: paymentLink || undefined, target: '_blank', rel: 'noopener noreferrer' }

  const quickFacts = [
    dateText && { icon: <CalendarMonthOutlinedIcon fontSize='small' />, label: t('quickFacts.date'), value: dateText },
    eventData.startTime && { icon: <ScheduleOutlinedIcon fontSize='small' />, label: t('quickFacts.startTime'), value: eventData.startTime },
    durationText && { icon: <TimerOutlinedIcon fontSize='small' />, label: t('quickFacts.duration'), value: durationText },
    eventData.location?.name && { icon: <PlaceOutlinedIcon fontSize='small' />, label: t('quickFacts.meetingPoint'), value: eventData.location.name }
  ].filter(Boolean)

  const statusChip = pricingStatus === 'closed'
    ? { label: eventData.status === 'ended' ? t('status.ended') : t('status.closed'), color: 'default' }
    : eventData.status === 'open'
      ? { label: t('status.open'), color: 'primary' }
      : eventData.status === 'soon'
        ? { label: t('status.soon'), color: 'secondary' }
        : { label: t('status.later'), color: 'default' }

  return (
    <>
      <HelmetMeta
        title={eventData.title}
        description={eventData.subtitle}
        image={sharePath}
        jsonLd={jsonLd}
      />

      <EventHeader eventData={eventData} status={statusChip} onShare={handleShare} />

      <Container maxWidth='lg' sx={{ pb: { xs: 14, md: 8 } }}>
        {/* Key facts, overlapping the hero */}
        {quickFacts.length > 0 && (
          <Paper
            sx={{
              position: 'relative',
              zIndex: 3,
              mt: { xs: -5, md: -6 },
              p: { xs: 2, md: 2.5 },
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: '0 18px 48px rgba(0,0,0,0.45)',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${quickFacts.length}, 1fr)` },
              gap: { xs: 2, md: 2.5 }
            }}
          >
            {quickFacts.map((fact, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', minWidth: 0 }}>
                <Box sx={{ color: 'primary.main', mt: '2px', flexShrink: 0 }}>{fact.icon}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='caption' sx={{ color: 'text.secondary', display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>
                    {fact.label}
                  </Typography>
                  <Typography variant='body2' sx={{ fontWeight: 600 }}>{fact.value}</Typography>
                </Box>
              </Box>
            ))}
          </Paper>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 380px' },
            gap: { xs: 3, lg: 4 },
            mt: { xs: 3, lg: 4 }
          }}
        >
          {/* Main column */}
          <Box sx={{ display: 'grid', gap: 3, alignContent: 'start', minWidth: 0 }}>
            {eventData.description && (
              <SectionCard icon={<EventNoteOutlinedIcon />} title={t('about')}>
                <Typography variant='body1' sx={{ color: 'text.secondary', lineHeight: 1.85, fontSize: { xs: '1rem', md: '1.08rem' } }}>
                  {eventData.description}
                </Typography>
              </SectionCard>
            )}

            {((eventData.organizedBy?.length > 0) || (eventData.hostedBy?.length > 0)) && (
              <SectionCard icon={<GroupsOutlinedIcon />} title={t('hostsAndOrganizers')}>
                {eventData.organizedBy?.length > 0 && (
                  <Box sx={{ mb: eventData.hostedBy?.length > 0 ? 3 : 0 }}>
                    <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>
                      {t('organizedBy')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
                      {eventData.organizedBy.map((person, i) => <PersonRow key={i} person={person} />)}
                    </Box>
                  </Box>
                )}
                {eventData.hostedBy?.length > 0 && (
                  <Box>
                    <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700 }}>
                      {t('hostedBy')}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, mt: 1.5 }}>
                      {eventData.hostedBy.map((person, i) => <PersonRow key={i} person={person} />)}
                    </Box>
                  </Box>
                )}
              </SectionCard>
            )}

            {eventData.details && (
              <SectionCard icon={<AutoAwesomeOutlinedIcon />} title={t('eventDetails')}>
                <EventDetails content={eventData.details} />
              </SectionCard>
            )}

            {bringItems.length > 0 && (
              <SectionCard icon={<CheckCircleRoundedIcon />} title={t('whatToBring')}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 1, sm: 1.5 } }}>
                  {bringItems.map((item, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                      <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'primary.main', mt: '3px', flexShrink: 0 }} />
                      <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.6 }}>{item}</Typography>
                    </Box>
                  ))}
                </Box>
              </SectionCard>
            )}

            {reserveNotice?.important && (
              <Paper
                component='section'
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'rgba(211,140,78,0.45)',
                  bgcolor: 'rgba(211,140,78,0.08)',
                  display: 'flex',
                  gap: 2
                }}
              >
                <WarningAmberRoundedIcon sx={{ color: 'secondary.main', flexShrink: 0 }} />
                <Box>
                  <Typography variant='h6' component='h2' sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 1 }}>
                    {t('importantInformation')}
                  </Typography>
                  <Typography variant='body2' sx={{ color: 'text.secondary', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                    {reserveNotice.important}
                  </Typography>
                </Box>
              </Paper>
            )}

            {eventData.contacts?.length > 0 && (
              <SectionCard icon={<ContactPhoneOutlinedIcon />} title={t('contact')}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                  {eventData.contacts.map((person, i) => <PersonRow key={i} person={person} />)}
                </Box>
              </SectionCard>
            )}
          </Box>

          {/* Sticky booking column */}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 96 } }}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  boxShadow: '0 18px 48px rgba(0,0,0,0.35)'
                }}
              >
                <Chip
                  label={statusChip.label}
                  color={statusChip.color}
                  size='small'
                  sx={{
                    fontWeight: 700,
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    fontSize: '0.68rem',
                    mb: 2
                  }}
                />

                {pricingStatus === 'closed'
                  ? (
                    <Box>
                      <Typography variant='h6' sx={{ fontWeight: 700, mb: 0.5 }}>
                        {eventData.status === 'ended' ? t('eventEnded') : t('spotsFilled')}
                      </Typography>
                      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                        {eventData.status === 'ended' ? t('eventEndedNote') : t('spotsFilledNote')}
                      </Typography>
                    </Box>
                    )
                  : (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.25, flexWrap: 'wrap' }}>
                        <Typography variant='h3' sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1 }}>
                          {formatPrice(activePrice)}
                        </Typography>
                        {strikePrice && (
                          <Typography variant='h6' sx={{ color: 'text.secondary', textDecoration: 'line-through', mb: 0.4 }}>
                            {formatPrice(strikePrice)}
                          </Typography>
                        )}
                      </Box>
                      <Typography variant='body2' sx={{ color: 'text.secondary', mt: 0.5 }}>
                        {pricingStatus === 'earlyBird' ? t('earlyBird') : t('regularPrice')}
                      </Typography>

                      {earlyBirdTimeRemaining && (
                        <Chip
                          icon={<HourglassBottomOutlinedIcon />}
                          label={t('earlyBirdEndsIn', { time: earlyBirdTimeRemaining })}
                          size='small'
                          color='secondary'
                          sx={{ mt: 1.5, fontWeight: 700 }}
                        />
                      )}
                      {earlyBirdEndFormatted && !earlyBirdTimeRemaining && pricingStatus !== 'earlyBird' && (
                        <Typography variant='caption' sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
                          {earlyBirdEndFormatted}
                        </Typography>
                      )}

                      <Button
                        fullWidth
                        size='large'
                        variant='contained'
                        disabled={!hasReserveNotice && !paymentLink}
                        {...reserveButtonProps}
                        sx={{
                          mt: 3,
                          py: 1.4,
                          borderRadius: 2,
                          fontWeight: 700,
                          fontSize: '1rem',
                          boxShadow: '0 10px 24px rgba(60,196,128,0.28)',
                          '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 14px 30px rgba(60,196,128,0.36)' }
                        }}
                      >
                        {t('reserveSpot')}
                      </Button>
                      <Typography variant='caption' sx={{ display: 'block', textAlign: 'center', color: 'text.secondary', mt: 1.25 }}>
                        {paymentLink ? t('stripeNote') : t('paymentComingSoon')}
                      </Typography>
                    </>
                    )}

                <Divider sx={{ my: 2.5 }} />

                <Stack spacing={2}>
                  <FactRow icon={<CalendarMonthOutlinedIcon fontSize='small' />} label={t('quickFacts.date')} value={dateText} />
                  <FactRow icon={<ScheduleOutlinedIcon fontSize='small' />} label={t('quickFacts.startTime')} value={eventData.startTime} />
                  <FactRow icon={<TimerOutlinedIcon fontSize='small' />} label={t('quickFacts.duration')} value={durationText} />
                  <FactRow
                    icon={<HourglassBottomOutlinedIcon fontSize='small' />}
                    label={t('quickFacts.arriveBy')}
                    value={eventData.beThereBefore ? t('beThereBefore', { minutes: eventData.beThereBefore }) : ''}
                  />
                  <FactRow icon={<PlaceOutlinedIcon fontSize='small' />} label={t('quickFacts.meetingPoint')} value={eventData.location?.name} />
                </Stack>

                <Button
                  fullWidth
                  startIcon={<ShareOutlinedIcon />}
                  onClick={handleShare}
                  sx={{ mt: 2.5, textTransform: 'none', borderRadius: 2, color: 'text.secondary' }}
                >
                  {t('share')}
                </Button>
              </Paper>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Mobile sticky action bar */}
      {pricingStatus !== 'closed' && (
        <Box
          ref={actionBarRef}
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1100,
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            px: 2,
            pt: 1.5,
            pb: 'calc(12px + env(safe-area-inset-bottom))',
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 -12px 32px rgba(0,0,0,0.45)'
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant='h6' sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1 }}>
                {formatPrice(activePrice)}
              </Typography>
              {strikePrice && (
                <Typography variant='caption' sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                  {formatPrice(strikePrice)}
                </Typography>
              )}
            </Box>
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              {pricingStatus === 'earlyBird' ? t('earlyBird') : t('regularPrice')}
            </Typography>
          </Box>
          <Button
            variant='contained'
            disabled={!hasReserveNotice && !paymentLink}
            {...reserveButtonProps}
            sx={{ borderRadius: 2, fontWeight: 700, px: 3, py: 1.2, flexShrink: 0 }}
          >
            {t('reserveSpot')}
          </Button>
        </Box>
      )}

      <Dialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{t('reserveConfirmTitle')}</DialogTitle>
        <DialogContent dividers>
          {bringItems.length > 0 && (
            <>
              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                {t('whatToBring')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                {bringItems.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 0.75 }}>
                    <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'primary.main', mt: '3px', flexShrink: 0 }} />
                    <Typography variant='body2'>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
          {reserveNotice?.important && (
            <>
              <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1 }}>
                {t('importantInformation')}
              </Typography>
              <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
                {reserveNotice.important}
              </Typography>
            </>
          )}
          <FormControlLabel
            sx={{ mt: 2, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
            }
            label={t('acknowledgeNotice')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenConfirm(false)}>{t('editor.cancel')}</Button>
          <Button
            variant='contained'
            disabled={!acknowledged}
            href={paymentLink || undefined}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => setOpenConfirm(false)}
          >
            {t('proceedToPayment')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
